// src/components/edge-nodes/EdgeNodesList.jsx
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Table from '../common/Table';
import Badge from '../common/Badge';
import { edgeNodesService } from '../../services/edge-nodes';

const EdgeNodesList = ({ filters = {} }) => {
  const [edgeNodes, setEdgeNodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchEdgeNodes = async () => {
      try {
        setLoading(true);
        const data = await edgeNodesService.getEdgeNodes(filters);
        setEdgeNodes(data);
        setError(null);
      } catch (err) {
        console.error('Error fetching edge nodes:', err);
        setError('Failed to load edge nodes. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchEdgeNodes();
  }, [filters]);

  // Format timestamp - Convert from UTC to CST (matching EventsList.jsx)
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'Never';
    
    // Add 'Z' to tell JavaScript this is UTC time
    const date = new Date(timestamp + 'Z');
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();
    
    if (isToday) {
      return date.toLocaleString('en-US', {
        timeZone: 'America/Chicago',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      });
    }
    
    return date.toLocaleString('en-US', {
      timeZone: 'America/Chicago',
      month: 'numeric',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  };

  // Helper function to determine real-time status based on heartbeat
  const getNodeStatus = (node) => {
    if (!node.last_heartbeat) {
      return { status: 'Offline', type: 'Offline' };
    }
    
    // Add 'Z' to force UTC interpretation
    const lastHeartbeat = new Date(node.last_heartbeat + 'Z');
    const now = new Date();
    const diffMinutes = (now - lastHeartbeat) / (1000 * 60);
    
    if (diffMinutes <= 3) {
      return { status: 'Online', type: 'Online' };
    } else {
      return { status: 'Offline', type: 'Offline' };
    }
  };

  const columns = [
    {
      header: 'Edge Node',
      accessor: 'hostname',
      render: (node) => (
        <div className="flex items-center">
          <div className="flex-shrink-0 h-10 w-10 bg-purple-100 text-purple-500 rounded-full flex items-center justify-center">
            <svg className="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01" />
            </svg>
          </div>
          <div className="ml-4">
            <div className="text-sm font-bold text-gray-900">
              {node.edge_node_id}
            </div>
          </div>
        </div>
      )
    },
    {
      header: 'Status',
      accessor: 'status',
      render: (node) => {
        const { status, type } = getNodeStatus(node);
        return <Badge type={type} text={status} />;
      }
    },
    {
      header: 'Hostname',
      accessor: 'hostname',
      render: (node) => (
        <div className="text-sm text-gray-900">
          {node.hostname || 'Not set'}
        </div>
      )
    },
    {
      header: 'Last Heartbeat',
      accessor: 'last_heartbeat',
      render: (node) => (
        <div className="text-sm text-gray-900">
          {formatTimestamp(node.last_heartbeat)}
        </div>
      )
    },
    {
      header: 'Assigned Policies',
      accessor: 'assigned_policies',
      render: (node) => (
        <div className="text-sm text-gray-900">
          {node.assigned_policies && node.assigned_policies.length > 0 
            ? `${node.assigned_policies.length} policies`
            : 'None'
          }
        </div>
      )
    },
    {
      header: 'Actions',
      render: (node) => (
        <div className="flex space-x-2">
          <Link
            to={`/edge-nodes/${node.edge_node_id}`}
            className="text-blue-600 hover:text-blue-900"
          >
            View
          </Link>
        </div>
      )
    }
  ];

  return (
    <div>
      {error && (
        <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      )}

      <Table
        columns={columns}
        data={edgeNodes}
        isLoading={loading}
        onRowClick={(node) => window.location.href = `/edge-nodes/${node.edge_node_id}`}
        emptyMessage="No edge nodes found matching your filters."
      />
    </div>
  );
};

export default EdgeNodesList;
